import React, { useEffect, useState } from "react";
import {
  LeftArrowIcon,
  InfoIcon,
  OpenBookIcon,
  NoteIcon,
  BlankPageIcon,
  ArrowDownIcon,
  PageNavigatorIcon,
} from "@/icons";
import { RightArrowIcon } from "@/icons/RightArrowIcon";
import { SavedPageIcon } from "@/icons/SavedPageIcon";
import { useTranslation } from 'react-i18next';
import {
  BookModel,
  DisplayTypes,
  PageCanvasModel,
  PageItemCanvasModel,
  PageNavigationDirection,
  SubToolType,
} from "@/types";
import { useSidebar } from "@/contexts/SideBarContext";
import { BlankPage, getUserPages } from "@/database/userPageDatabaseService";
import { User } from "@/database/userDatabaseService";
import BlankPageTools from "./BlankPageTools";
import FloatingButton from "./AddNote";
import TestTools from "./TestTools";
import "./index.scss";
import ActivityTools from "./ActivityTools";
import SmartBookTour from "../SmartBookTour";
import BookContents from "@/pages/bookDetail/BookContents";
import { Tools } from "@/@corefabriccanvas";
import { useNavigate } from "react-router-dom";

type BottomToolProps = {
  book: BookModel;
  user: User;
  currentPage: PageCanvasModel;
  currentPageItem: PageItemCanvasModel | null;
  displayType: DisplayTypes;
  setPage: (page: number) => void;
  isBottomToolOpen: boolean;
  setIsBottomToolOpen: (isOpen: boolean) => void;
  sbStore: any; // `sbStore` için tam tür belirlemesi yapılmalı
  setDisplayType: (type: DisplayTypes) => void;
  isSavedPageOpen: boolean;
  toggleSavedPage: () => void;
  dispatch: any;
  setBlankPages: (pages: BlankPage[]) => void;
  setCurrentBlankPage: (blankPage: BlankPage) => void;
  canvas: any; // Canvas tipi için tam bir tür ekleyin
  completeActivity: () => void;
  resetActivity: () => void;
  showActivityAnswer: () => void;
  showPreviousAnswer: () => void;
  showNextAnswer: () => void;
  absolutePan: (isNext: boolean) => boolean;
  handlePageChange: (direction: PageNavigationDirection) => void;
  previousDisplayType: DisplayTypes;
  currentBlankPage: BlankPage;
  blankPages: BlankPage[];
  toggleNumerator: () => void;
  pageItemPosition: "left" | "right" | "center";
  updatePageItemPosition: (position: "left" | "right" | "center") => void;
  currentTool: Tools;
  selectedColor: string;
  previousTool: Tools;
  screenWidth: number;
  setTool: (tool: Tools) => void;
  setSelectedColor: (color: string, userId: string) => void;
  thickness: number;
  setThickness: (thickness: number, userId: string) => void;
  eraseThickness: number;
  setEraserThickness: (thickness: number, userId: string) => void;
  updateActiveSubTool: (subTool: SubToolType | undefined) => void;
  activeSubTool: SubToolType | undefined;
};

const BottomTool: React.FC<BottomToolProps> = ({
  book,
  user,
  currentPage,
  currentPageItem,
  displayType,
  setPage,
  dispatch,
  setDisplayType,
  setBlankPages,
  handlePageChange,
  previousDisplayType,
  currentBlankPage,
  blankPages,
  canvas,
  setCurrentBlankPage,
  toggleSavedPage,
  setIsBottomToolOpen,
  isBottomToolOpen,
  toggleNumerator,
  pageItemPosition,
  updatePageItemPosition,
  showActivityAnswer,
  showPreviousAnswer,
  showNextAnswer,
  resetActivity,
  completeActivity,
  currentTool,
  selectedColor,
  previousTool,
  setTool,
  setSelectedColor,
  thickness,
  setThickness,
  eraseThickness,
  setEraserThickness,
  updateActiveSubTool,
  activeSubTool,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'footer' });
  const { openSidebar } = useSidebar();
  const [showGapFillings, setShowGapFillings] = React.useState(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    console.log("bottom-tool-rendering");
  });

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Detect iOS device
    const checkIsIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };
    
    setIsIOS(checkIsIOS());

    // Apply iOS viewport fix if needed
    if (checkIsIOS()) {
      // Add CSS variable with the safe area inset
      document.documentElement.style.setProperty(
        '--safe-area-inset-bottom', 
        `${window.innerHeight - document.documentElement.clientHeight}px`
      );
      
      // Listen for resize events to update the safe area
      const handleResize = () => {
        document.documentElement.style.setProperty(
          '--safe-area-inset-bottom', 
          `${window.innerHeight - document.documentElement.clientHeight}px`
        );
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  useEffect(() => {
    setShowGapFillings(true);
  }, [currentPageItem]);

  const openBlankPage = async () => {
    const pages = await getUserPages(user.id, book.id, true);
    dispatch(setBlankPages(pages));
    dispatch(setCurrentBlankPage(pages[0]));
    dispatch(setDisplayType(DisplayTypes.BlankPage));
  };

  const openBookContents = () => {
    openSidebar(
      "bookContents",
      `${t("book_contents")}`,
      "left",
      <BookContents getBookPage={setPage} bookContents={book?.units || []} />
    );
  };

  const collapseSecondLayer = () => {
    canvas.collapseSecondLayer(showGapFillings);
    setShowGapFillings(!showGapFillings);
  };

  const collapseGapFillings = () => {
    canvas.collapseAllGapFilling(showGapFillings);
    setShowGapFillings(!showGapFillings);
  };
  return (
    <>
      <div
        className={`collapse-bottom-tool-button ${isIOS ? 'ios-fixed' : ''}`}
        onClick={() => setIsBottomToolOpen(!isBottomToolOpen)}
      >
        <ArrowDownIcon className={undefined} />
      </div>

      <div className={`bottom-tool-left w-40 ${isIOS ? 'ios-fixed' : ''}`}>
        <button
          className="obro-btn obro-light-gray-btn"
          onClick={() => navigate(`/book-detail/${book.id}`)}
        >
          <InfoIcon className={undefined} />
          {t("book_info")}
        </button>
        <button
          className="obro-btn obro-light-gray-btn book-content-btn"
          onClick={() => openBookContents()}
        >
          <OpenBookIcon className={undefined} color={undefined} />
          {t("book_contents")}
        </button>
      </div>
      <div className={`bottom-tool-center w-20 ${isIOS ? 'ios-fixed' : ''}`}>
        <div className="navigation-area">
          <button
            className="obro-btn obro-transparent-btn border-right"
            onClick={() => handlePageChange(PageNavigationDirection.Previous)}
          >
            <LeftArrowIcon className={undefined} />
          </button>
          {(displayType === DisplayTypes.Page ||
            displayType === DisplayTypes.PageItem) &&
            currentPage && (
              <>
                <button
                  className="obro-btn obro-transparent-btn navigator-btn"
                  onClick={toggleNumerator}
                >
                  <PageNavigatorIcon className={undefined} />
                </button>
                <div className="page-navigation">
                  <span>{`${currentPage?.pageNo} / ${book.pages.length}`}</span>
                </div>
              </>
            )}
          <button
            className="obro-btn obro-transparent-btn border-left"
            onClick={() => handlePageChange(PageNavigationDirection.Next)}
          >
            <RightArrowIcon className={undefined} />
          </button>
        </div>
      </div>
      <div className={`bottom-tool-right w-40 ${isIOS ? 'ios-fixed' : ''}`}>
        <FloatingButton>
          <NoteIcon className={undefined} />
          {t("note_add")}
        </FloatingButton>
        <button
          className="obro-btn obro-light-gray-btn saved-page-btn"
          onClick={() => toggleSavedPage()}
        >
          <SavedPageIcon className={undefined} />
          {t("pages")}
        </button>
        <button
          className="obro-btn obro-light-gray-btn white-board-btn"
          onClick={() => openBlankPage()}
        >
          <BlankPageIcon className={undefined} color={undefined} />
          {t("whiteboard")}
        </button>
        <SmartBookTour />
      </div>
      {displayType === DisplayTypes.BlankPage && (
        <BlankPageTools
          dispatch={dispatch}
          user={user}
          book={book}
          setBlankPages={setBlankPages}
          setCurrentBlankPage={setCurrentBlankPage}
          canvas={canvas}
          previousDisplayType={previousDisplayType}
          currentBlankPage={currentBlankPage}
          blankPages={blankPages}
        />
      )}
      {displayType === DisplayTypes.PageItem &&
        currentPageItem &&
        currentPageItem?.pageItemType < 70 && (
          <TestTools
            currentPageItem={currentPageItem}
            displayType={displayType}
            dispatch={dispatch}
            setDisplayType={setDisplayType}
            pageItemPosition={pageItemPosition}
            updatePageItemPosition={updatePageItemPosition}
            collapseGapFillings={collapseGapFillings}
            collapseSecondLayer={collapseSecondLayer}
          />
        )}
      {displayType === DisplayTypes.PageItem &&
        currentPageItem &&
        currentPageItem?.pageItemType > 70 && (
          <ActivityTools
            dispatch={dispatch}
            currentPageItem={currentPageItem}
            setDisplayType={setDisplayType}
            completeActivity={completeActivity}
            resetActivity={resetActivity}
            showActivityAnswer={showActivityAnswer}
            showPreviousAnswer={showPreviousAnswer}
            showNextAnswer={showNextAnswer}
          />
        )}
    </>
  );
};

export default React.memo(BottomTool);
